import { useState, useEffect, useMemo, useRef } from "react";
import "../Styles/Dashboard.css";
import TopNav from "../layouts/TopNav";
import {
  AiOutlineRise,
  AiOutlineInbox,
  AiOutlineCodeSandbox,
} from "react-icons/ai";
import { PiNoteDuotone } from "react-icons/pi";
import { IoIosCash } from "react-icons/io";
import { FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { CircularProgress, Skeleton, Stack } from "@mui/material";
import ReLoginModal from "../components/ReLoginModal";
import api from "../util/axios";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const chartOptions = {
  scales: {
    x: {
      title: {
        display: true,
        text: "Day of Month",
      },
    },
    y: {
      title: {
        display: true,
        text: "Count",
      },
      beginAtZero: true,
    },
  },
};

interface Requisition {
  _id: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  price: number;
  quantity: number;
  loadingSlip?: any;
  customer?: any;
  firstName: string;
  staff?: any;
  business: string;
  items: any[];
  businessType: string;
  status?: string;
}

interface LoadingSlip {
  _id: string;
  createdAt: string;
  updatedAt: string;
  invoiced: boolean;
  voided: boolean;
}

interface ReportData {
  requisition: Array<{
    allRequisitions: number;
    approvedRequisition: number;
    unApprovedRequisition: number;
    cancelledRequisition: number;
  }>;
  loadingSlip: Array<{
    allLoadingSlip: number;
    invoicedLoadingSlip: number;
    notInvoicedLoadingSlip: number;
    voidedLoadingSlip: number;
  }>;
}

const DashboardHome = () => {
  const token = window.localStorage.getItem("token");
  const userRole = window.localStorage.getItem("userRole");
  const [reLogin, setReLogin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loadingSlips, setLoadingSlips] = useState<LoadingSlip[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showCurrentMonth, setShowCurrentMonth] = useState(true);

  // Cache report data to avoid refetching
  const reportDataCache = useRef<ReportData | null>(null);

  // Debounce state for refresh
  const lastRefresh = useRef<number>(0);

  // Memoized chart data
  const chartData = useMemo(() => {
    const requisitionsByDate: { [key: string]: number } = {};
    requisitions.forEach((req) => {
      const date = new Date(req.updatedAt).getDate().toString();
      requisitionsByDate[date] = (requisitionsByDate[date] || 0) + 1;
    });

    const firstDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    const dateLabels: string[] = [];
    for (
      let date = new Date(firstDayOfMonth);
      date <= lastDayOfMonth;
      date.setDate(date.getDate() + 1)
    ) {
      dateLabels.push(date.getDate().toString());
    }

    const data = dateLabels.map((day) => requisitionsByDate[day] || 0);

    return {
      labels: dateLabels,
      datasets: [
        {
          label: "Requisitions Created",
          data,
          fill: true,
          borderColor: "rgba(138, 82, 250, 1)",
          backgroundColor: "rgba(157, 117, 236, 1)",
        },
      ],
    };
  }, [requisitions, selectedMonth]);

  const loadingSlipChartData = useMemo(() => {
    const loadingSlipsByDate: { [key: string]: number } = {};
    loadingSlips.forEach((slip) => {
      const date = new Date(slip.updatedAt).getDate().toString();
      loadingSlipsByDate[date] = (loadingSlipsByDate[date] || 0) + 1;
    });

    const firstDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    const dateLabels: string[] = [];
    for (
      let date = new Date(firstDayOfMonth);
      date <= lastDayOfMonth;
      date.setDate(date.getDate() + 1)
    ) {
      dateLabels.push(date.getDate().toString());
    }

    const data = dateLabels.map((day) => loadingSlipsByDate[day] || 0);

    return {
      labels: dateLabels,
      datasets: [
        {
          label: "Loading Slips Created",
          data,
          fill: true,
          borderColor: "rgba(75,192,192,1)",
          backgroundColor: "rgba(75,192,192,0.5)",
        },
      ],
    };
  }, [loadingSlips, selectedMonth]);

  // Fetch general report data
  const fetchReport = async () => {
    if (!token) {
      setReLogin(true);
      return;
    }

    // Use cached data if available
    if (reportDataCache.current) {
      setReportData(reportDataCache.current);
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await api.get("/report", { headers });
      const data = response.data.data;
      reportDataCache.current = data;
      setReportData(data);
    } catch (error: any) {
      if (!error.response?.data) {
        toast.error("Network challenges");
      }
      if (error.response?.data?.message === "jwt expired" || error.response?.data?.message === "jwt malformed") {
        toast.error("Session expired, please log in again.");
        setReLogin(true);
      } else if (error.response?.data?.message === "invalid user permission") {
        setError("You do not have access to this data.");
      } else {
        setError("Failed to fetch report data. Please try again.");
      }
    }
  };

  // Fetch requisitions for a given month
  const fetchRequisitions = async (monthDate: Date) => {
    if (!token) {
      setReLogin(true);
      return;
    }

    if (userRole === "staff") {
      setRequisitions([]);
      return;
    }

    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const startDate = firstDayOfMonth.toISOString().split("T")[0];
    const endDate = lastDayOfMonth.toISOString().split("T")[0];

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await api.get(
        `https://apibiz.oneflaretech.com/api/requisition?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      );
      const fetchedRequisitions: Requisition[] = response.data.data?.requisitions || [];
      setRequisitions(fetchedRequisitions);
    } catch (error: any) {
      if (error.response?.data?.message === "jwt expired" || error.response?.data?.message === "jwt malformed") {
        toast.error("Session expired, please log in again.");
        setReLogin(true);
      } else if (error.response?.data?.message === "invalid user permission") {
        setError("You do not have access to this data.");
      } else {
        setError("Failed to fetch requisitions. Please try again.");
      }
    }
  };

  // Fetch loading slips for a given month
  const fetchLoadingSlips = async (monthDate: Date) => {
    if (!token) {
      setReLogin(true);
      return;
    }

    if (userRole === "staff") {
      setLoadingSlips([]);
      return;
    }

    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const startDate = firstDayOfMonth.toISOString().split("T")[0];
    const endDate = lastDayOfMonth.toISOString().split("T")[0];

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await api.get(
        `/loading-slip?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      );
      const fetchedLoadingSlips: LoadingSlip[] = response.data.data || [];
      setLoadingSlips(fetchedLoadingSlips);
    } catch (error: any) {
      if (error.response?.data?.message === "jwt expired" || error.response?.data?.message === "jwt malformed") {
        toast.error("Session expired, please log in again.");
        setReLogin(true);
      } else if (error.response?.data?.message === "invalid user permission") {
        setError("You do not have access to this data.");
      } else {
        setError("Failed to fetch loading slips. Please try again.");
      }
    }
  };

  // Compute current month overview from requisitions and loading slips
  const computeCurrentMonthOverview = (requisitions: Requisition[], loadingSlips: LoadingSlip[]): ReportData => {
    const requisitionStats = { allRequisitions: 0, approvedRequisition: 0, unApprovedRequisition: 0, cancelledRequisition: 0 };
    requisitions.forEach(req => {
      requisitionStats.allRequisitions++;
      const status = req.status?.toLowerCase();
      if (status === "approved") requisitionStats.approvedRequisition++;
      else if (status === "awaiting") requisitionStats.unApprovedRequisition++;
      else if (status === "rejected") requisitionStats.cancelledRequisition++;
    });

    const loadingSlipStats = { allLoadingSlip: 0, invoicedLoadingSlip: 0, notInvoicedLoadingSlip: 0, voidedLoadingSlip: 0 };
    loadingSlips.forEach(slip => {
      loadingSlipStats.allLoadingSlip++;
      if (slip.invoiced) loadingSlipStats.invoicedLoadingSlip++;
      else if (!slip.invoiced && !slip.voided) loadingSlipStats.notInvoicedLoadingSlip++;
      else if (slip.voided) loadingSlipStats.voidedLoadingSlip++;
    });

    return {
      requisition: [requisitionStats],
      loadingSlip: [loadingSlipStats],
    };
  };

  // Fetch data on mount and when selected month changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const promises = [fetchRequisitions(selectedMonth), fetchLoadingSlips(selectedMonth)];
      if (!showCurrentMonth && !reportDataCache.current) {
        promises.push(fetchReport());
      }
      await Promise.all(promises);
      setIsLoading(false);
    };
    fetchData();
  }, [selectedMonth, showCurrentMonth]);

  // Handle toggle between current month and general overview
  const handleToggleOverview = () => {
    setShowCurrentMonth((prev) => !prev);
  };

  // Handle refresh with debounce
  const handleRefresh = () => {
    const now = Date.now();
    if (now - lastRefresh.current < 1000) return; // Debounce: 1-second delay
    lastRefresh.current = now;
    setError(null);
    reportDataCache.current = null; // Clear cache on refresh
    fetchReport();
    fetchRequisitions(selectedMonth);
    fetchLoadingSlips(selectedMonth);
  };

  // Handle month navigation
  const handlePreviousMonth = () => {
    setSelectedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setSelectedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  // Format month for display
  const formatMonth = (date: Date) => {
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  // Determine which data to display in the overview
  const overviewData = useMemo(
    () => (showCurrentMonth ? computeCurrentMonthOverview(requisitions, loadingSlips) : reportData),
    [showCurrentMonth, requisitions, loadingSlips, reportData]
  );

  return (
    <div className="board">
      <TopNav title="Dashboard" />
      {reLogin && <ReLoginModal />}
      <div className="boardBody">
        {error && <div className="error-message">{error}</div>}
        {isLoading ? (
          <Stack spacing={1}>
            <div className="flex gap-4">
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="15%" />
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="5%" />
            </div>
            <div className="flex gap-8">
              <Skeleton animation="wave" variant="rectangular" width={410} height={200} />
              <Skeleton animation="wave" variant="rounded" width={410} height={200} />
            </div>
            <div className="flex justify-center gap-1">
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="10%" />
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="10%" />
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="10%" />
            </div>
            <div className="flex justify-center">
              <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="10%" />
            </div>
            <div className="flex gap-4 w-full">
              <Skeleton variant="rounded" width="50%" animation="wave" height="40vh" />
              <Skeleton variant="rounded" width="50%" animation="wave" height="40vh" />
            </div>
          </Stack>
        ) : (
          <>
            <div className="overview-header flex gap-6 mb-2 items-center">
              <h2>{showCurrentMonth ? "Current Month Overview" : "General Overview"}</h2>
              <button onClick={handleToggleOverview} className="p-1 px-2 bg-blue-400 hover:text-gray-900 text-white rounded-xl">
                {showCurrentMonth ? "Show All" : "Show Current Month"}
              </button>
            </div>
            <div className="overview">
              <div className="overCon">
                <div className="viewItem">
                  <div className="viewIcon">
                    <PiNoteDuotone className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Total Loading Slips</h4>
                    <p>{overviewData?.loadingSlip[0]?.allLoadingSlip ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <AiOutlineRise className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Invoiced Loading Slips</h4>
                    <p>{overviewData?.loadingSlip[0]?.invoicedLoadingSlip ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <IoIosCash className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Not Invoiced Loading Slips</h4>
                    <p>{overviewData?.loadingSlip[0]?.notInvoicedLoadingSlip ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <IoIosCash className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Voided Loading Slips</h4>
                    <p>{overviewData?.loadingSlip[0]?.voidedLoadingSlip ?? "N/A"}</p>
                  </div>
                </div>
              </div>
              <div className="overCon">
                <div className="viewItem">
                  <div className="viewIcon">
                    <AiOutlineInbox className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Total Requisitions</h4>
                    <p>{overviewData?.requisition[0]?.allRequisitions ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <AiOutlineCodeSandbox className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Approved Requisitions</h4>
                    <p>{overviewData?.requisition[0]?.approvedRequisition ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <AiOutlineCodeSandbox className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Awaiting Requisitions</h4>
                    <p>{overviewData?.requisition[0]?.unApprovedRequisition ?? "N/A"}</p>
                  </div>
                </div>
                <div className="viewItem">
                  <div className="viewIcon">
                    <FiRefreshCw className="viewIcons" />
                  </div>
                  <div className="viewText">
                    <h4>Rejected Requisitions</h4>
                    <p>{overviewData?.requisition[0]?.cancelledRequisition ?? "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="month-navigation">
              <button onClick={handlePreviousMonth}>Previous Month</button>
              <span>{formatMonth(selectedMonth)}</span>
              <button onClick={handleNextMonth}>Next Month</button>
            </div>
            <button onClick={handleRefresh} className="refresh-button">
              Refresh Data
            </button>

            {userRole !== "staff" ? (
              <>
                <div className="charts">
                  <div className="table">
                    <Line className="table1" data={loadingSlipChartData} options={chartOptions} />
                  </div>
                  <div className="table">
                    <Bar data={loadingSlipChartData} options={chartOptions} />
                  </div>
                </div>
                <div className="charts">
                  <div className="table">
                    <Line className="table1" data={chartData} options={chartOptions} />
                  </div>
                  <div className="table">
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                </div>
              </>
            ) : (
              <div className="restricted-message">
                <p>Chart data is only available for admin users.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div >
  );
};

export default DashboardHome;